import React from "react";
import { ArrowLeft, Tag, Info, Calendar, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("it-IT", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

function ServiceContent() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-12 w-2/3 bg-muted rounded-md" />
          <div className="h-px w-full bg-muted" />
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <div className="h-6 w-full bg-muted rounded-md" />
              <div className="h-6 w-5/6 bg-muted rounded-md" />
              <div className="h-6 w-4/5 bg-muted rounded-md" />
            </div>
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="bg-destructive/10 text-destructive p-6 rounded-xl inline-block">
          <h3 className="text-lg font-semibold mb-2">Errore nel caricamento</h3>
          <p className="mb-4">Non è stato possibile caricare il servizio.</p>
          <Button onClick={refetch} variant="outline">Riprova</Button>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Nessun servizio trovato.</p>
        <div className="mt-4">
            <Link to="/">
                <Button variant="secondary">Torna alla Home</Button>
            </Link>
        </div>
      </div>
    );
  }

  
  const post = posts[0];
  const { title, content, customFields, date } = post;
  
  
  const description = customFields?.description;
  const tag = customFields?.tag;

  return (
    <div className="container mx-auto px-4 py-12 md:py-16" data-index={0}>
      {}
      <div className="mb-8">
        <Link to="/services">
          <Button variant="ghost" size="sm" className="pl-0 hover:pl-1 transition-all">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Torna ai Servizi
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-8 space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {title}
        </h1>
        {tag && (
            <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-sm px-3 py-1">
                    {tag}
                </Badge>
            </div>
        )}
      </header>

      <Separator className="my-8" />

      <div className="grid gap-10 lg:grid-cols-3 lg:gap-12">
        {}
        <div className="lg:col-span-2 space-y-8">
          {}
          {description && (
            <section className="prose prose-lg dark:prose-invert max-w-none">
              <h2 className="text-2xl font-semibold mb-4 text-foreground flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" />
                Descrizione del Servizio
              </h2>
              <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {description}
              </div>
            </section>
          )}

          {}
          {content && (
            <section 
                className="prose prose-lg dark:prose-invert max-w-none mt-8"
                dangerouslySetInnerHTML={{ __html: content }} 
            />
          )}
        </div>

        {}
        <aside className="space-y-6">
          <Card className="sticky top-24 border-border/60 shadow-sm">
            <CardHeader className="bg-muted/30 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tag className="h-4 w-4 text-primary" />
                Dettagli
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 pt-6">
              {tag && (
                <div className="grid gap-1">
                  <span className="text-sm font-medium text-muted-foreground">Categoria</span>
                  <span className="font-medium">{tag}</span>
                </div>
              )}
              
              {date && (
                <div className="grid gap-1">
                  <span className="text-sm font-medium text-muted-foreground">Data Pubblicazione</span>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{formatDate(date)}</span>
                  </div>
                </div>
              )}

              <Separator className="my-2" />
              
              <div className="grid gap-3">
                <Button className="w-full" size="lg">
                  Richiedi Informazioni
                </Button>
                <Button variant="outline" className="w-full" size="lg">
                  <Share2 className="mr-2 h-4 w-4" />
                  Condividi
                </Button>
              </div>
            </CardContent>
            <CardFooter className="bg-muted/30 text-xs text-muted-foreground py-3">
              Servizio professionale garantito
            </CardFooter>
          </Card>
        </aside>
      </div>
    </div>
  );
}

export default function SingleServiceTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-service-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <ServiceContent />
      </PostsProvider>
    </section>
  );
}